import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, InputAdornment, LinearProgress, MenuItem, Select, TablePagination, TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Team } from '../../models/types';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { SnackbarState } from './useContestActions';
import { RosterRow, searchKey, useTeamRoster } from './useTeamRoster';
import { ALL, TeamRosterFilterChips, UNASSIGNED } from './TeamRosterFilterChips';
import { TeamDeleteDialog } from './TeamDeleteDialog';

interface TeamRosterTableProps {
  contestId: string;
  groupId: string;
  teams: Team[];
  // locked: the contest has ended. 	eamsLocked: it has started, so teams can no longer be deleted.
  locked: boolean;
  teamsLocked: boolean;
  onChanged: () => void;
  notify: (message: string, severity: SnackbarState['severity']) => void;
}

const COLUMNS = '40px minmax(180px,2fr) minmax(160px,1fr)';
const headSx = { fontSize: 12, fontWeight: 500, color: 'var(--muted)' } as const;

// Admin table of every group member and their team in the contest: filter, search, reassign per row or in bulk.
export const TeamRosterTable = ({ contestId, groupId, teams, locked, teamsLocked, onChanged, notify }: TeamRosterTableProps) => {
  const { t } = useTranslation('contests');
  const { rows, loading, loadError, pending, assign } = useTeamRoster(groupId, contestId, teams, onChanged);
  const [filter, setFilter] = useState(ALL);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  const sortedTeams = useMemo(() => [...teams].sort((a, b) => a.name.localeCompare(b.name)), [teams]);
  const counts = useMemo(() => {
    const byTeam = new Map<string, number>();
    rows.forEach(row => byTeam.set(row.teamId ?? UNASSIGNED, (byTeam.get(row.teamId ?? UNASSIGNED) ?? 0) + 1));
    return byTeam;
  }, [rows]);

  const visible = useMemo(() => {
    const needle = searchKey(query.trim());
    return rows.filter(row =>
      (filter === ALL || (filter === UNASSIGNED ? row.teamId === null : row.teamId === filter)) &&
      (!needle || searchKey(`${row.name} ${row.secondary}`).includes(needle))
    );
  }, [rows, filter, query]);

  // Selection only ever covers what is listed, so a bulk move never touches hidden rows.
  useEffect(() => {
    setPage(0);
    setSelected(new Set());
  }, [filter, query]);
  // A deleted team can no longer be the filter or the bulk target.
  useEffect(() => {
    const exists = (id: string) => teams.some(team => team._id === id);
    if (filter !== ALL && filter !== UNASSIGNED && !exists(filter)) setFilter(ALL);
    if (bulkTarget && bulkTarget !== UNASSIGNED && !exists(bulkTarget)) setBulkTarget('');
  }, [teams, filter, bulkTarget]);
  // Moving rows out of the current filter can shrink the list below the current page.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(visible.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [visible.length, rowsPerPage, page]);

  const pageRows = visible.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const allVisibleSelected = visible.length > 0 && visible.every(row => selected.has(row.userId));
  const someVisibleSelected = visible.some(row => selected.has(row.userId));
  const busy = pending.size > 0;

  const toggleAllVisible = () =>
    setSelected(prev => {
      const next = new Set(prev);
      visible.forEach(row => (allVisibleSelected ? next.delete(row.userId) : next.add(row.userId)));
      return next;
    });

  const toggleRow = (userId: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (!next.delete(userId)) next.add(userId);
      return next;
    });

  const toTeamId = (value: string) => (value === UNASSIGNED ? null : value);

  const changeRow = async (row: RosterRow, value: string) => {
    const outcome = await assign([row.userId], toTeamId(value));
    if (outcome.failures.length > 0) {
      notify(t('notify.assignError', { name: row.name, reason: outcome.failures[0].reason }), 'error');
    }
  };

  const applyBulk = async () => {
    const outcome = await assign(Array.from(selected), toTeamId(bulkTarget));
    setSelected(new Set(outcome.failures.map(failure => failure.userId)));
    if (outcome.failures.length === 0) {
      notify(t('notify.membersUpdated', { count: outcome.ok }), 'success');
    } else {
      notify(t('notify.membersUpdatePartial', { count: outcome.ok, failed: outcome.failures.length, reason: outcome.failures[0].reason }), 'warning');
    }
  };

  const teamOptions = [
    <MenuItem key={UNASSIGNED} value={UNASSIGNED}>
      <Box component="span" sx={{ color: 'var(--muted)' }}>{t('roster.unassigned')}</Box>
    </MenuItem>,
    ...sortedTeams.map(team => <MenuItem key={team._id} value={team._id}>{team.name}</MenuItem>),
  ];

  if (loadError) {
    return <Box sx={{ p: 2, color: 'var(--danger)' }}>{t('roster.loadError')}</Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', gap: 1.5, borderBottom: '1px solid var(--border)' }}>
        {teamsLocked && (
          <Box sx={{ fontSize: 13, color: 'var(--accent-fg)', backgroundColor: 'var(--accent-bg)', borderRadius: '10px', p: '10px 14px' }}>{t(locked ? 'roster.locked' : 'roster.running')}</Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            size="small"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t('roster.searchPlaceholder')}
            sx={{ flex: '1 1 260px', maxWidth: 420 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <Box sx={{ fontSize: 13, color: 'var(--muted)' }}>
            {t('roster.summary', { assigned: rows.length - (counts.get(UNASSIGNED) ?? 0), total: rows.length })}
          </Box>
        </Box>
        <TeamRosterFilterChips teams={sortedTeams} counts={counts} total={rows.length} value={filter} onChange={setFilter} onDeleteTeam={teamsLocked ? undefined : setTeamToDelete} />
        {!locked && selected.size > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', p: '8px 12px', borderRadius: '10px', backgroundColor: 'var(--muted-bg)' }}>
            <Box sx={{ fontSize: 13, fontWeight: 500, mr: 1 }}>{t('roster.selectedCount', { count: selected.size })}</Box>
            <Select size="small" displayEmpty value={bulkTarget} onChange={event => setBulkTarget(event.target.value)} sx={{ minWidth: 180 }} inputProps={{ 'aria-label': t('roster.moveTo') }}>
              <MenuItem value="" disabled>{t('roster.moveTo')}</MenuItem>
              {teamOptions}
            </Select>
            <Button variant="contained" size="small" onClick={applyBulk} disabled={!bulkTarget || busy} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
              {t('roster.apply')}
            </Button>
            <Button size="small" onClick={() => setSelected(new Set())} disabled={busy}>{t('roster.clearSelection')}</Button>
          </Box>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 420 }} role="table" aria-label={t('roster.tableLabel')}>
            <Box role="row" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, alignItems: 'center', p: '4px 16px', borderBottom: '1px solid var(--border)', ...headSx }}>
              <Checkbox size="small" checked={allVisibleSelected} indeterminate={someVisibleSelected && !allVisibleSelected} onChange={toggleAllVisible} disabled={locked || visible.length === 0} inputProps={{ 'aria-label': t('roster.selectAll') }} />
              <span role="columnheader">{t('roster.columns.member')}</span>
              <span role="columnheader">{t('roster.columns.team')}</span>
            </Box>
            {pageRows.length === 0 && <Box sx={{ p: '20px 16px', fontSize: 13, color: 'var(--muted)', textAlign: 'center' }}>{t('roster.noMatches')}</Box>}
            {pageRows.map(row => (
              <Box key={row.userId} role="row" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, alignItems: 'center', p: '6px 16px', borderBottom: '1px solid var(--border)', backgroundColor: selected.has(row.userId) ? 'var(--muted-bg)' : undefined }}>
                <Checkbox size="small" checked={selected.has(row.userId)} onChange={() => toggleRow(row.userId)} disabled={locked} inputProps={{ 'aria-label': t('roster.selectMember', { name: row.name }) }} />
                <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <InitialsAvatar name={row.name} size={28} />
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</Box>
                    {row.secondary && <Box sx={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.secondary}</Box>}
                  </Box>
                </Box>
                <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Select size="small" fullWidth value={row.teamId ?? UNASSIGNED} onChange={event => changeRow(row, event.target.value)} disabled={locked || pending.has(row.userId)} inputProps={{ 'aria-label': t('roster.teamFor', { name: row.name }) }}>
                    {teamOptions}
                  </Select>
                  {pending.has(row.userId) && <CircularProgress size={16} />}
                </Box>
              </Box>
            ))}
          </Box>
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage={t('roster.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('roster.displayedRows', { from, to, count })}
          />
        </Box>
      )}

      <TeamDeleteDialog
        team={teamToDelete}
        memberCount={teamToDelete ? counts.get(teamToDelete._id) ?? 0 : 0}
        onCancel={() => setTeamToDelete(null)}
        onDeleted={team => {
          setTeamToDelete(null);
          onChanged();
          notify(t('notify.teamDeleted', { team: team.name }), 'success');
        }}
        onError={message => notify(message, 'error')}
      />
    </Box>
  );
};
